import { useCallback, useMemo, useState } from 'react';

import { createWorkflow } from 'api/workflowsClient';
import type { WorkflowDto } from 'model/workflow';
import { workflowToXyflow, xyflowToWorkflow } from 'model/xyflowMapping';
import type { WorkflowGraph } from 'model/xyflowMapping';
import { clearCachedGraph, loadCachedGraph } from 'utils/workflowCache';

export interface UseEditorPersistenceArgs {
  initialWorkflowId: string;
  initialWorkflow: WorkflowDto;
  /** Current xyflow graph — needed for save-as-new and copy-id flows. */
  graph: WorkflowGraph;
  /** From `useWorkflowGraphState` — replaces the editor graph wholesale. */
  resetGraph: (next: WorkflowGraph, dirty: boolean) => void;
  /** Clears the page-level selection (e.g. after a Reset). */
  clearSelection: () => void;
}

export interface UseEditorPersistenceResult {
  workflowId: string;
  workflow: WorkflowDto;
  setWorkflow: (next: WorkflowDto | ((prev: WorkflowDto) => WorkflowDto)) => void;
  isSaving: boolean;
  error: string | null;
  statusMessage: string | null;
  showJsonEditor: boolean;
  openJsonEditor: () => void;
  closeJsonEditor: () => void;
  handleReset: () => void;
  handleDiscardLocalChanges: () => void;
  handleSaveAsNew: () => Promise<void>;
  handleJsonSave: (updatedWorkflow: WorkflowDto) => void;
  handleCopyWorkflowId: () => void;
}

/**
 * Owns the page-level "workflow lifecycle" state: which workflow is
 * loaded, the in-progress save flag, the user-visible error/status
 * banners, and the JSON-editor modal toggle.
 *
 * The handlers all rotate around the same five state slots, so keeping
 * them together cuts ~60 lines of orchestration glue out of the page.
 *
 * Initial status banner: shows "Restored unsaved changes…" if the local
 * cache has an entry for the workflow at mount time — matches the
 * pre-hook behaviour exactly.
 */
export const useEditorPersistence = ({
  initialWorkflowId,
  initialWorkflow,
  graph,
  resetGraph,
  clearSelection,
}: UseEditorPersistenceArgs): UseEditorPersistenceResult => {
  const [workflowId, setWorkflowId] = useState(initialWorkflowId);
  const [workflow, setWorkflow] = useState<WorkflowDto>(initialWorkflow);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(() =>
    loadCachedGraph(initialWorkflowId) ? 'Restored unsaved changes from local cache' : null,
  );
  const [showJsonEditor, setShowJsonEditor] = useState(false);

  const openJsonEditor = useCallback(() => setShowJsonEditor(true), []);
  const closeJsonEditor = useCallback(() => setShowJsonEditor(false), []);

  const resetTo = useCallback(
    (target: WorkflowDto, message: string) => {
      clearCachedGraph(workflowId);
      setWorkflow(target);
      resetGraph(workflowToXyflow(target), false);
      clearSelection();
      setError(null);
      setStatusMessage(message);
    },
    [clearSelection, resetGraph, workflowId],
  );

  const handleReset = useCallback(
    () => resetTo(initialWorkflow, 'Reset to originally loaded workflow'),
    [initialWorkflow, resetTo],
  );

  const handleDiscardLocalChanges = useCallback(
    () => resetTo(initialWorkflow, 'Local changes discarded'),
    [initialWorkflow, resetTo],
  );

  const handleSaveAsNew = useCallback(async () => {
    setIsSaving(true);
    setError(null);
    setStatusMessage(null);
    try {
      const updatedWorkflow = xyflowToWorkflow(workflow.name, graph);
      const result = await createWorkflow(updatedWorkflow);
      clearCachedGraph(workflowId);
      setWorkflow(result.workflow);
      setWorkflowId(result.workflowId);
      resetGraph(workflowToXyflow(result.workflow), false);
      setStatusMessage(`Saved as new workflow: ${result.workflowId}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create workflow');
    } finally {
      setIsSaving(false);
    }
  }, [graph, resetGraph, workflow.name, workflowId]);

  const handleJsonSave = useCallback(
    (updatedWorkflow: WorkflowDto) => {
      setWorkflow(updatedWorkflow);
      resetGraph(workflowToXyflow(updatedWorkflow), true);
      setShowJsonEditor(false);
      setStatusMessage('JSON applied');
    },
    [resetGraph],
  );

  const handleCopyWorkflowId = useCallback(() => {
    void navigator.clipboard.writeText(workflowId).then(() => {
      setStatusMessage('Workflow ID copied to clipboard');
    });
  }, [workflowId]);

  return useMemo(
    () => ({
      workflowId,
      workflow,
      setWorkflow,
      isSaving,
      error,
      statusMessage,
      showJsonEditor,
      openJsonEditor,
      closeJsonEditor,
      handleReset,
      handleDiscardLocalChanges,
      handleSaveAsNew,
      handleJsonSave,
      handleCopyWorkflowId,
    }),
    [
      workflowId,
      workflow,
      isSaving,
      error,
      statusMessage,
      showJsonEditor,
      openJsonEditor,
      closeJsonEditor,
      handleReset,
      handleDiscardLocalChanges,
      handleSaveAsNew,
      handleJsonSave,
      handleCopyWorkflowId,
    ],
  );
};
